/**
 * Converts a React-Router-style `RouteObject[]` config into a TanStack Router
 * route tree + router instance.
 *
 * This is the single point where the codebase's existing code-based route
 * config (kept verbatim in `routes.tsx` and the `*-routes.tsx` modules) is
 * translated to TanStack. Keeping the conversion generic means the route
 * modules don't change shape and there's one place to get the translation
 * right.
 *
 * Translation rules (React Router -> TanStack):
 * - `{ index: true }`              -> index route (`path: '/'`, no children)
 * - `{ path: '' | '/' }`           -> pathless layout route (matched by `id`)
 * - `:param`                       -> `$param`
 * - `:param?` (trailing, optional) -> split into two routes (with / without)
 * - trailing `*` / `/*`            -> `$` splat
 * - `element`                      -> `component` (rendered as-is; layouts keep
 *                                     their own `<Outlet />`)
 */
import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router';
import { isValidElement } from 'react';

import { joinPaths } from './path-utils';
import { RouteContext } from './route-context';

import type { AnyRoute, AnyRouter, RouterHistory } from '@tanstack/react-router';
import type { ReactNode } from 'react';

export type RouteObject = {
  path?: string;
  index?: boolean;
  element?: ReactNode;
  children?: RouteObject[];
};

/** Translate a single relative RR path segment-string to TanStack token syntax. */
function toTanstackPath(relativePath: string): string {
  // Trailing splat: `foo/*` -> `foo/$`, bare `*` -> `$`.
  let path = relativePath.replace(/\/\*$/, '/$').replace(/^\*$/, '$');
  // Required params: `:id` -> `$id`.
  path = path.replace(/:([\w-]+)/g, '$$$1');
  return path;
}

/** A trailing optional param like `participants/:segmentId?`. */
function splitOptionalParam(relativePath: string): { withoutParam: string; withParam: string } | null {
  const match = relativePath.match(/^(.*?)\/?:([\w-]+)\?$/);
  if (!match) {
    return null;
  }
  const prefix = match[1] ?? '';
  const paramName = match[2];
  return {
    withoutParam: prefix,
    withParam: prefix ? `${prefix}/$${paramName}` : `$${paramName}`,
  };
}

type Preload = () => Promise<unknown>;

const hasPreload = (type: unknown): type is { preload: Preload } =>
  typeof type === 'function' && 'preload' in type && typeof type.preload === 'function';

function collectPreloads(node: ReactNode): Preload[] {
  if (Array.isArray(node)) {
    return node.flatMap(collectPreloads);
  }
  if (!isValidElement<{ children?: ReactNode }>(node)) {
    return [];
  }
  const childPreloads = collectPreloads(node.props.children);
  return hasPreload(node.type) ? [node.type.preload, ...childPreloads] : childPreloads;
}

// The router waits for `component.preload` before it renders the match, so the current page stays on
// screen while a `lazyRouteComponent` chunk loads. A suspending render instead shows the pending
// component for at least React's 300 ms fallback throttle, even when the chunk loads faster.
// A first load has no current page, so it suspends: the layout paints while the chunk loads.
const elementComponent = (element: ReactNode, routes: readonly AnyRoute[], isFirstLoad: () => boolean) => {
  const Component = () => <RouteContext.Provider value={routes}>{element}</RouteContext.Provider>;
  const preloads = collectPreloads(element);
  if (preloads.length) {
    Component.preload = async () => {
      const loaded = Promise.all(preloads.map(preload => preload()));
      if (!isFirstLoad()) {
        await loaded;
      } else {
        // The lazy component throws the load error on render, so the route error boundary shows it.
        loaded.catch(() => {});
      }
    };
  }
  return Component;
};

let layoutIdCounter = 0;

type BuildContext = { parentFullPath: string; isFirstLoad: () => boolean };

/** Compute a node's full RR path (used to derive its TanStack-relative path). */
function fullPathOf(node: RouteObject, parentFullPath: string): string {
  if (node.index || node.path == null || node.path === '' || node.path === '/') {
    return parentFullPath;
  }
  if (node.path.startsWith('/')) {
    return node.path;
  }
  return joinPaths([parentFullPath, node.path]);
}

/** Strip the parent's full path prefix to get the path relative to the TanStack parent. */
function relativePathOf(fullPath: string, parentFullPath: string): string {
  const base = parentFullPath === '/' ? '' : parentFullPath;
  const relative = fullPath.startsWith(base) ? fullPath.slice(base.length) : fullPath;
  return relative.replace(/^\/+/, '');
}

function buildRoute(node: RouteObject, parentRoute: AnyRoute, ctx: BuildContext): AnyRoute[] {
  const routes: AnyRoute[] = [];
  const component = node.element !== undefined ? elementComponent(node.element, routes, ctx.isFirstLoad) : Outlet;
  const track = (route: AnyRoute): AnyRoute => {
    routes.push(route);
    return route;
  };
  const fullPath = fullPathOf(node, ctx.parentFullPath);
  const childCtx: BuildContext = { ...ctx, parentFullPath: fullPath };

  const buildChildren = (route: AnyRoute): AnyRoute => {
    if (node.children?.length) {
      route.addChildren(node.children.flatMap(child => buildRoute(child, route, childCtx)));
    }
    return route;
  };

  // Index route.
  if (node.index) {
    return [track(createRoute({ getParentRoute: () => parentRoute, path: '/', component }))];
  }

  // Pathless layout route (RR `path: ''` or `path: '/'`).
  if (node.path == null || node.path === '' || node.path === '/') {
    const route = createRoute({ getParentRoute: () => parentRoute, id: `_layout${layoutIdCounter++}`, component });
    return [buildChildren(track(route))];
  }

  const relativePath = relativePathOf(fullPath, ctx.parentFullPath);

  // Trailing optional param -> two sibling routes sharing element + children.
  const optional = splitOptionalParam(relativePath);
  if (optional) {
    const withoutParam = optional.withoutParam
      ? createRoute({ getParentRoute: () => parentRoute, path: toTanstackPath(optional.withoutParam), component })
      : createRoute({ getParentRoute: () => parentRoute, path: '/', component });
    const withParam = createRoute({
      getParentRoute: () => parentRoute,
      path: toTanstackPath(optional.withParam),
      component,
    });
    return [buildChildren(track(withoutParam)), buildChildren(track(withParam))];
  }

  const route = createRoute({ getParentRoute: () => parentRoute, path: toTanstackPath(relativePath), component });
  return [buildChildren(track(route))];
}

/**
 * Convert an RR-style config into TanStack routes parented on `rootRoute`,
 * without attaching them. The app router uses this to graft the shim tree onto
 * the file-based (generated) root as runtime-only children; the tests' own
 * roots go through `buildRouteTree` below.
 */
export function buildRouteChildren(
  routes: RouteObject[],
  rootRoute: AnyRoute,
  { isFirstLoad = () => false }: { isFirstLoad?: () => boolean } = {},
): AnyRoute[] {
  layoutIdCounter = 0;
  return routes.flatMap(route => buildRoute(route, rootRoute, { parentFullPath: '/', isFirstLoad }));
}

/**
 * Build a TanStack route tree (root + children) from an RR-style config.
 *
 * `rootComponent` becomes the root route's component — tests omit it and get a
 * bare passthrough `Outlet`.
 */
function buildRouteTree(routes: RouteObject[], rootComponent?: () => ReactNode): AnyRoute {
  const rootRoute = createRootRoute({ component: rootComponent ?? Outlet });
  rootRoute.addChildren(buildRouteChildren(routes, rootRoute));
  return rootRoute;
}

type CreateRouterOptions = {
  history?: RouterHistory;
  rootComponent?: () => ReactNode;
};

/**
 * Build a TanStack router from an RR-style config.
 *
 * Returned as `AnyRouter`: runtime-built trees are invisible to the registered
 * (file-based) router type, so an inferred return would resolve
 * `state.matches` against the registered tree and collapse it to `never`.
 */
export function createRouterFromRoutes(routes: RouteObject[], options: CreateRouterOptions = {}): AnyRouter {
  const routeTree = buildRouteTree(routes, options.rootComponent);
  return createRouter({
    routeTree,
    history: options.history,
    // Match React Router defaults: no preloading, no auto scroll restoration.
    defaultPreload: false,
    scrollRestoration: false,
    // The RR config provides its own catch-all (`path: '*'`) routes.
    defaultNotFoundComponent: () => null,
  });
}
